<script lang="ts">
  import { onMount } from 'svelte';
  import { login } from '../lib/auth.svelte';
  import { isPilotMode } from '../lib/pilot';
  import { isKeycloakMode } from '../lib/keycloak';
  import { DEMO_PLAN_OPTIONS } from '../lib/hospital-preview.svelte';

  const pilot = isPilotMode();
  const showForm = isKeycloakMode() && !pilot;
  const MARKETING = 'https://www.toscanasystems.com/pages/sistema-medico/hospital-privado.php';
  const LOGO = 'https://www.toscanasystems.com/assets/img/logo.png';

  let usuario = $state('');
  let password = $state('');
  let showPass = $state(false);
  let err = $state<string|null>(null);
  let busy = $state(false);

  let forgot = $state(false);
  let forgotStep = $state<'user'|'code'|'done'>('user');
  let forgotEmail = $state('');
  let forgotCode = $state('');
  let forgotPass = $state('');
  let forgotMsg = $state('');
  let forgotErr = $state<string|null>(null);
  let forgotLoading = $state(false);

  onMount(() => {
    const prev = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme','light');
    return () => {
      if(prev) document.documentElement.setAttribute('data-theme',prev);
      else document.documentElement.removeAttribute('data-theme');
    };
  });

  async function submit(e:SubmitEvent) {
    e.preventDefault();err=null;busy=true;
    try {
      await login(undefined,usuario.trim(),password);
      if(typeof location!=='undefined') location.replace('/');
    }catch(e:unknown){
      const msg = e instanceof Error?e.message:'No se pudo iniciar sesión';
      err=msg;
    }finally{busy=false;}
  }

  async function startForgot(e:SubmitEvent) {
    e.preventDefault();forgotErr=null;forgotLoading=true;
    try {
      const r = await fetch('/api/auth/v1/public/forgot-password', {
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({email:forgotEmail.trim()})});
      if(!r.ok){const b:any=await r.json().catch(()=>({error:'Error'}));throw new Error(b.error??'Error');}
      const d=await r.json() as {email:string;mensaje:string;code?:string};
      if(d.code){forgotMsg='Su codigo es: '+d.code;forgotCode=d.code;}
      else{forgotMsg=d.mensaje;}
      forgotStep='code';
    }catch(e:unknown){forgotErr=e instanceof Error?e.message:'Error';}
    finally{forgotLoading=false;}
  }

  async function completeForgot(e:SubmitEvent) {
    e.preventDefault();forgotErr=null;forgotLoading=true;
    try {
      const r = await fetch('/api/auth/v1/public/reset-password',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({email:forgotEmail.trim(),code:forgotCode.trim(),newPassword:forgotPass})});
      if(!r.ok){const b:any=await r.json().catch(()=>({error:'Error'}));throw new Error(b.error??'Error');}
      forgotStep='done';
    }catch(e:unknown){forgotErr=e instanceof Error?e.message:'Error';}
    finally{forgotLoading=false;}
  }

  function backToLogin(){forgot=false;forgotStep='user';forgotEmail='';forgotCode='';forgotPass='';forgotMsg='';forgotErr=null;}
</script>

<div class="root">
  <div class="split-l">
    <div class="l-bg"></div>
    <div class="l-deco l-deco-1"></div>
    <div class="l-deco l-deco-2"></div>
    <div class="l-deco l-deco-3"></div>

    <div class="l-inner">
      <a class="l-brand" href="/" rel="noopener">
        <img src={LOGO} alt="" />
        <div class="l-brand-text">
          <strong>Toscana Hospital</strong>
          <span>Software hospitalario</span>
        </div>
      </a>

      <div class="l-hero">
        <h2>Todo listo para <span>gestionar su hospital</span></h2>
        <p>Camas, quirófano, farmacia y facturación FEL en una sola plataforma.</p>
      </div>

      <ul class="l-features">
        <li>
          <span class="lf-icon">&#8203;</span>
          Gestión de camas y hospitalización
        </li>
        <li>
          <span class="lf-icon">&#8203;</span>
          Quirófano y programación
        </li>
        <li>
          <span class="lf-icon">&#8203;</span>
          Facturación FEL y aseguradoras
        </li>
      </ul>

      <div class="l-foot">
        ¿Aún sin cuenta? <a href="/registro">Regístrese — 30 días gratis</a>
      </div>
    </div>
  </div>

  <div class="split-r">
    <div class="r-card">
      {#if pilot}
        <div class="r-head">
          <h1>Demos · hospital privado</h1>
          <p>Seleccione un plan de ejemplo para explorar el sistema.</p>
        </div>
        <div class="demo-grid">
          {#each DEMO_PLAN_OPTIONS as o}
            <a class="demo-chip" href="/demo/{o.slug}">
              <span class="dc-emoji">{o.emoji}</span>
              <span class="dc-label">{o.label}</span>
            </a>
          {/each}
        </div>
        <p class="r-sub">
          <a href="/registro">Crear hospital propio &rarr;</a>
        </p>

      {:else if showForm}
        {#if forgot}
          <div class="r-head">
            <h1>Restablecer contraseña</h1>
            {#if forgotStep === 'user'}
              <p>Ingrese el correo con el que se registró para recibir un código de verificación.</p>
            {:else if forgotStep === 'code'}
              <p>{forgotMsg}</p>
            {:else}
              <p>Contraseña actualizada correctamente.</p>
            {/if}
          </div>

          {#if forgotStep === 'user'}
            <form onsubmit={startForgot}>
              <div class="field">
                <label for="fg-email">Correo registrado</label>
                <input id="fg-email" type="email" bind:value={forgotEmail} placeholder="admin@hospital.gt" required autocomplete="email" />
              </div>
              {#if forgotErr}<div class="msg msg-err">{forgotErr}</div>{/if}
              <button type="submit" class="btn" disabled={forgotLoading}>
                {forgotLoading?'Enviando…':'Enviar código'}
              </button>
            </form>
          {:else if forgotStep === 'code'}
            <form onsubmit={completeForgot}>
              <div class="field">
                <label for="fg-code">Código de verificación</label>
                <input id="fg-code" type="text" inputmode="numeric" bind:value={forgotCode} placeholder="000000" required maxlength="6" minlength="6" class="code-input" />
              </div>
              <div class="field">
                <label for="fg-pass">Nueva contraseña</label>
                <input id="fg-pass" type="password" bind:value={forgotPass} placeholder="Mínimo 6 caracteres" required minlength="6" autocomplete="new-password" />
              </div>
              {#if forgotErr}<div class="msg msg-err">{forgotErr}</div>{/if}
              <button type="submit" class="btn" disabled={forgotLoading}>
                {forgotLoading?'Cambiando…':'Cambiar contraseña'}
              </button>
            </form>
          {:else}
            <a class="btn btn-link" href="/login" onclick={backToLogin}>Iniciar sesión</a>
          {/if}

          <p class="r-sub">
            <button type="button" class="link-btn" onclick={backToLogin}>← Volver al inicio de sesión</button>
          </p>

        {:else}
          <div class="r-head">
            <h1>Iniciar sesión</h1>
            <p>Ingrese con su cuenta <strong>@toscanasystems.com</strong></p>
          </div>

          <form onsubmit={submit}>
            <div class="field">
              <label for="f-email">Correo electrónico</label>
              <input id="f-email" type="email" autocomplete="username" bind:value={usuario} placeholder="su-hospital@toscanasystems.com" required />
            </div>

            <div class="field">
              <label for="f-pass">Contraseña</label>
              <div class="pass-box">
                <input id="f-pass" type={showPass?'text':'password'} autocomplete="current-password" bind:value={password} placeholder="Ingrese su contraseña" required />
                <button type="button" class="pass-tog" onclick={()=>showPass=!showPass} tabindex="-1">
                  {showPass?'Ocultar':'Mostrar'}
                </button>
              </div>
            </div>

            {#if err}
              <div class="msg msg-err">{err}</div>
            {/if}

            <button type="submit" class="btn" disabled={busy}>
              {busy?'Verificando…':'Entrar al panel'}
            </button>
          </form>

          <p class="r-sub">
            <button type="button" class="link-btn" onclick={()=>{forgot=true;forgotStep='user';forgotErr=null}}>¿Olvidó su contraseña?</button>
            <span class="r-sub-sep">·</span>
            <a href="/registro">Crear cuenta</a>
          </p>
        {/if}

      {:else}
        <div class="r-head">
          <h1>Iniciar sesión</h1>
          <p>Autenticación no configurada en este entorno.</p>
        </div>
        <a class="btn btn-link" href="/registro">Crear cuenta</a>
        <p class="r-sub">
          <a href={MARKETING} target="_blank" rel="noopener">Ver planes &rarr;</a>
        </p>
      {/if}
    </div>

    <div class="r-foot">
      Toscana Systems, S.A. · <a href={MARKETING} target="_blank" rel="noopener">toscanasystems.com</a>
    </div>
  </div>
</div>

<style>
  :global(body) {
    margin: 0;
    background: #f8fafc;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: #1e293b;
    -webkit-font-smoothing: antialiased;
  }

  .root {
    display: grid;
    grid-template-columns: minmax(340px,42%) 1fr;
    min-height: 100vh;
  }
  @media (max-width: 780px) {
    .root { grid-template-columns: 1fr; }
  }

  /* ═══════════════ LEFT PANEL ═══════════════ */
  .split-l {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(165deg, #064e3b 0%, #0c4a44 30%, #0f5e57 65%, #166534 100%);
    color: #fff;
  }

  .l-bg {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 80%, rgba(45,156,156,0.14) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(45,212,191,0.06) 0%, transparent 50%);
  }

  .l-deco {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.04);
    pointer-events: none;
  }
  .l-deco-1 {
    width: 300px; height: 300px;
    top: -60px; right: -80px;
  }
  .l-deco-2 {
    width: 200px; height: 200px;
    bottom: 10%; left: -40px;
  }
  .l-deco-3 {
    width: 140px; height: 140px;
    top: 45%; right: 15%;
    border-color: rgba(255,255,255,0.06);
  }

  .l-inner {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 360px;
    padding: 56px 40px;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    box-sizing: border-box;
  }

  .l-brand {
    display: flex;
    align-items: center;
    gap: 14px;
    text-decoration: none;
    margin-bottom: 48px;
    padding-bottom: 32px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
  }
  .l-brand img {
    height: 44px;
    width: auto;
    filter: brightness(0) invert(1);
    opacity: 0.92;
  }
  .l-brand-text {
    display: flex;
    flex-direction: column;
  }
  .l-brand strong {
    font-size: 19px;
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.03em;
    line-height: 1.2;
  }
  .l-brand span {
    font-size: 11px;
    color: rgba(255,255,255,0.45);
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .l-hero { margin-bottom: 48px; }
  .l-hero h2 {
    font-size: 26px;
    font-weight: 700;
    color: #f1f5f9;
    letter-spacing: -0.04em;
    line-height: 1.2;
    margin: 0 0 14px;
  }
  .l-hero h2 span {
    display: block;
    color: #5eead4;
  }
  .l-hero p {
    font-size: 14px;
    color: rgba(255,255,255,0.55);
    line-height: 1.65;
    margin: 0;
    max-width: 320px;
  }

  .l-features {
    list-style: none;
    margin: 0 0 48px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .l-features li {
    display: flex;
    align-items: center;
    gap: 14px;
    font-size: 13.5px;
    color: rgba(255,255,255,0.65);
    font-weight: 500;
  }
  .lf-icon {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
    position: relative;
  }
  .lf-icon::after {
    content: '';
    position: absolute;
    left: 7px;
    top: 5px;
    width: 8px;
    height: 5px;
    border-left: 2px solid #5eead4;
    border-bottom: 2px solid #5eead4;
    transform: rotate(-45deg);
  }

  .l-foot {
    margin-top: auto;
    padding-top: 24px;
    border-top: 1px solid rgba(255,255,255,0.08);
    font-size: 13px;
    color: rgba(255,255,255,0.4);
  }
  .l-foot a,
  .l-foot a:visited {
    color: rgba(255,255,255,0.65);
    font-weight: 600;
    text-decoration: none;
    transition: color 0.15s;
  }
  .l-foot a:hover { color: #5eead4; }

  @media (max-width: 780px) {
    .split-l { display: none; }
  }

  /* ═══════════════ RIGHT PANEL ═══════════════ */
  .split-r {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 32px;
    background: #f8fafc;
  }

  .r-card {
    width: 100%;
    max-width: 400px;
    background: #fff;
    border: 1px solid rgba(0,0,0,0.05);
    border-radius: 20px;
    padding: 40px;
    box-shadow:
      0 1px 2px rgba(0,0,0,0.02),
      0 8px 24px rgba(0,0,0,0.04);
  }
  @media (max-width: 780px) {
    .split-r { padding: 32px 20px; }
    .r-card { padding: 32px 24px; }
  }

  .r-head { margin-bottom: 28px; }
  .r-head h1 {
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 6px;
    letter-spacing: -0.04em;
  }
  .r-head p {
    font-size: 14px;
    color: #64748b;
    margin: 0;
    line-height: 1.5;
  }
  .r-head strong { color: #16a34a; font-weight: 600; }

  .r-sub {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid rgba(0,0,0,0.05);
    text-align: center;
    font-size: 13.5px;
    color: #64748b;
  }
  .r-sub a,
  .r-sub a:visited {
    color: #16a34a;
    font-weight: 600;
    text-decoration: none;
    transition: color 0.15s;
    margin-left: 2px;
  }
  .r-sub a:hover { color: #22c55e; }
  .r-sub .link-btn {
    background: none;
    border: 0;
    color: #16a34a;
    font: inherit;
    font-size: inherit;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    text-decoration: none;
  }
  .r-sub .link-btn:hover { color: #22c55e; }
  .r-sub-sep { margin: 0 6px; color: #cbd5e1; }

  .code-input {
    font-size: 28px !important;
    letter-spacing: 14px !important;
    text-align: center !important;
    font-family: 'JetBrains Mono','SF Mono','Cascadia Code',monospace !important;
    padding: 14px !important;
  }

  /* Fields */
  .field { margin-bottom: 20px; }
  .field label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 7px;
    letter-spacing: -0.01em;
  }
  .field input {
    width: 100%;
    box-sizing: border-box;
    padding: 10px 14px;
    border: 1.5px solid rgba(0,0,0,0.08);
    border-radius: 10px;
    font: inherit;
    font-size: 14px;
    color: #0f172a;
    background: #fff;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .field input::placeholder { color: #cbd5e1; }
  .field input:focus {
    outline: none;
    border-color: #22c55e;
    box-shadow: 0 0 0 3px rgba(20,184,166,0.1);
  }

  .pass-box { position: relative; }
  .pass-box input { padding-right: 72px; }
  .pass-tog {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: 0;
    color: #64748b;
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 6px;
    transition: all 0.15s;
  }
  .pass-tog:hover { color: #16a34a; background: #f0fdfa; }

  /* Messages */
  .msg {
    font-size: 13px;
    padding: 10px 14px;
    border-radius: 10px;
    margin-bottom: 20px;
    line-height: 1.5;
  }
  .msg-err {
    color: #dc2626;
    background: #fef2f2;
    border: 1px solid #fecaca;
  }

  /* Button */
  .btn {
    display: block;
    width: 100%;
    padding: 13px;
    border: 0;
    border-radius: 10px;
    background: #1e293b;
    color: #fff;
    font: inherit;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    margin-top: 24px;
    letter-spacing: -0.01em;
    transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
    box-sizing: border-box;
    text-align: center;
    text-decoration: none;
  }
  .btn:hover:not(:disabled) {
    background: #0f172a;
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  }
  .btn:active:not(:disabled) { transform: scale(0.985); }
  .btn:disabled { opacity: 0.45; cursor: not-allowed; }
  .btn-link { display: flex; align-items: center; justify-content: center; }

  /* Demo grid */
  .demo-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 8px;
  }
  .demo-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    border: 1.5px solid rgba(0,0,0,0.06);
    border-radius: 10px;
    text-decoration: none;
    color: #1e293b;
    font-size: 13px;
    font-weight: 600;
    background: #fff;
    transition: all 0.15s;
    letter-spacing: -0.01em;
  }
  .demo-chip:hover {
    border-color: #16a34a;
    background: #f0fdfa;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(15,118,110,0.06);
  }
  .dc-emoji { font-size: 15px; line-height: 1; flex-shrink: 0; }
  .dc-label { flex: 1; }
  @media (max-width: 400px) {
    .demo-grid { grid-template-columns: 1fr; }
  }

  /* Footer */
  .r-foot {
    margin-top: 20px;
    font-size: 12px;
    color: #94a3b8;
    text-align: center;
  }
  .r-foot a,
  .r-foot a:visited {
    color: #16a34a;
    font-weight: 600;
    text-decoration: none;
    transition: color 0.15s;
  }
  .r-foot a:hover { color: #22c55e; }
</style>
